import { record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { produce } from 'immer';

type QueryParamValue = string | boolean | number | object;

type ClientRequestConfig = {
  method: string;
  url: string;
  params?: Record<string, QueryParamValue>;
  data?: unknown;
  responseType?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

type RequestConfig = ClientRequestConfig & {
  // query params handling happens inside apiClient function and is passed to client implementation
  // converted to the query string to the underlying client.
  params?: Record<string, QueryParamValue>;
};

type ClientFunction = <T>(config: ClientRequestConfig) => Promise<T>;
let client: ClientFunction | null = null;

export const setApiClient = (apiClient: ClientFunction | null) => {
  client = apiClient;
};

const convertParamsToUrl: (config: RequestConfig) => ClientRequestConfig =
  produce((draft) => {
    if (draft.params && Object.keys(draft.params).length > 0) {
      const params = pipe(
        draft.params,
        record.map((value) =>
          typeof value === 'object' ? JSON.stringify(value) : String(value),
        ),
      );
      draft.url += `?${new URLSearchParams(params).toString()}`;
      // biome-ignore lint/performance/noDelete: immer mutation
      delete draft.params;
    }
  });

export const apiClient = <T>(config: RequestConfig): Promise<T> => {
  if (!client) {
    throw new Error('apiClient not set');
  }
  return client(convertParamsToUrl(config));
};

export const testable = {
  convertParamsToUrl,
};
